import { createTheme } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { BREAKPOINTS } from './breakpoints';
import { CRM_COLORS, CRM_RADIUS } from './crmColors';

export const APP_COLOR_PRIMARY = CRM_COLORS.primary;
export const APP_COLOR_BACKGROUND = CRM_COLORS.background;
export const APP_ITEM_SELECTED_BACKGROUND_COLOR_ON_LIST = CRM_COLORS.rowSelected;

export const APP_BUTTON_COLOR_PRIMARY = CRM_COLORS.primary;
export const APP_TEXT_COLOR_PRIMARY = CRM_COLORS.primary;
export const APP_COLOR_PRIMARY_CHIP = APP_COLOR_PRIMARY;

export const APP_BAR_MAX_HEIGHT = 64;

export const APP_FONT_FAMILY = [
    '"Figtree"',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'sans-serif',
].join(',');

export const hexToRGBA = (hex, opacity) => {
    return (
        'rgba(' +
        (hex = hex.replace('#', ''))
            .match(new RegExp('(.{' + hex.length / 3 + '})', 'g'))
            .map(function (l) {
                return parseInt(hex.length % 2 ? l + l : l, 16);
            })
            .concat(opacity)
            .join(',') +
        ')'
    );
};

const theme = createTheme({
    palette: {
        primary: {
            main: APP_COLOR_PRIMARY,
            dark: CRM_COLORS.primaryDark,
            light: CRM_COLORS.primaryLight,
            contrastText: CRM_COLORS.surface,
        },
        secondary: {
            main: CRM_COLORS.secondary,
            contrastText: CRM_COLORS.surface,
        },
        error: {
            main: CRM_COLORS.error,
        },
        warning: {
            main: CRM_COLORS.warning,
        },
        success: {
            main: CRM_COLORS.success,
        },
        info: {
            main: CRM_COLORS.info,
        },
        background: {
            default: APP_COLOR_BACKGROUND,
            paper: CRM_COLORS.surface,
        },
        text: {
            primary: CRM_COLORS.text.primary,
            secondary: CRM_COLORS.text.secondary,
            disabled: CRM_COLORS.text.disabled,
        },
        divider: CRM_COLORS.border,
    },
    typography: {
        fontFamily: APP_FONT_FAMILY,
        h1: { fontWeight: 700, letterSpacing: '-0.015em', color: CRM_COLORS.secondary },
        h2: { fontWeight: 700, letterSpacing: '-0.015em', color: CRM_COLORS.secondary },
        h3: { fontWeight: 700, color: CRM_COLORS.secondary },
        h4: { fontWeight: 700, color: CRM_COLORS.secondary },
        h5: { fontWeight: 700, color: CRM_COLORS.secondary },
        h6: { fontWeight: 700, color: CRM_COLORS.secondary },
        button: { fontWeight: 600, textTransform: 'none' },
    },
    shape: {
        borderRadius: CRM_RADIUS.control,
    },
    overrides: {
        MuiCssBaseline: {},
        MuiToolbar: {
            root: {
                'regular + &': {
                    marginTop: 'auto',
                    backgroundColor: 'red',
                },
            },
        },
        RaSaveButton: {
            button: {
                color: APP_BUTTON_COLOR_PRIMARY,
                backgroundColor: 'white',
                border: `1px solid ${hexToRGBA(APP_BUTTON_COLOR_PRIMARY, 0.45)}`,
                borderColor: APP_BUTTON_COLOR_PRIMARY,
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': {
                    color: 'white',
                    backgroundColor: APP_BUTTON_COLOR_PRIMARY,
                    boxShadow: CRM_COLORS.shadow,
                },
            },
        },
        RaToolbar: {
            toolbar: {
                backgroundColor: 'white',
                paddingRight: '0px !important',
                paddingLeft: '0px !important',
            },
            defaultToolbar: {
                justifyContent: 'flex-end',
            },
        },
        RaEdit: {
            noActions: {
                marginTop: '0px !important',
            },
        },
        MuiAppBar: {
            root: {
                boxShadow: 'none !important',
            },
            colorPrimary: {
                color: APP_COLOR_PRIMARY,
                backgroundColor: 'white',
            },
        },
        MuiCheckbox: {
            colorPrimary: {
                color: CRM_COLORS.text.secondary,
                '&$checked': {
                    color: APP_COLOR_PRIMARY,
                    backgroundColor: 'white',
                },
            },
        },
        MuiCardContent: {
            root: {
                paddingBottom: '0px !important',
            },
        },
        MuiPaper: {
            rounded: {
                borderRadius: CRM_RADIUS.control,
            },
            elevation1: {
                boxShadow: CRM_COLORS.shadow,
            },
            elevation8: {
                boxShadow: CRM_COLORS.shadowPopover,
            },
        },
        MuiCard: {
            root: {
                borderRadius: CRM_RADIUS.card,
                border: `1px solid ${CRM_COLORS.border}`,
                boxShadow: CRM_COLORS.shadow,
            },
        },
        MuiBackdrop: {
            root: {
                backgroundColor: 'rgba(9, 43, 75, 0.46)',
            },
        },
        MuiDialog: {
            paper: {
                borderRadius: CRM_RADIUS.dialog,
                boxShadow: CRM_COLORS.shadowDialog,
            },
        },
        MuiDialogTitle: {
            root: {
                padding: '22px 26px 8px',
                color: CRM_COLORS.secondary,
                '& .MuiTypography-root': {
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    letterSpacing: '-0.015em',
                    lineHeight: 1.25,
                },
            },
        },
        MuiDialogContent: {
            root: {
                padding: '12px 26px 8px',
            },
        },
        MuiButton: {
            root: {
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                borderRadius: CRM_RADIUS.control,
            },
            textSizeSmall: {
                fontSize: '0.875rem',
                padding: '8px 12px',
            },
            outlined: {
                borderColor: CRM_COLORS.borderStrong,
                color: CRM_COLORS.secondary,
            },
            containedPrimary: {
                backgroundColor: APP_BUTTON_COLOR_PRIMARY,
                color: 'white',
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: CRM_COLORS.primaryDark,
                    boxShadow: 'none',
                },
            },
            outlinedPrimary: {
                color: APP_BUTTON_COLOR_PRIMARY,
                borderColor: APP_BUTTON_COLOR_PRIMARY,
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: hexToRGBA(APP_COLOR_PRIMARY, 0.06),
                    borderColor: CRM_COLORS.primaryDark,
                },
            },
        },
        MuiTab: {
            root: {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9375rem',
            },
            textColorPrimary: {
                color: CRM_COLORS.text.secondary,
                '&$selected': {
                    color: APP_TEXT_COLOR_PRIMARY,
                },
            },
        },
        MuiTabs: {
            indicator: {
                backgroundColor: APP_TEXT_COLOR_PRIMARY,
                height: 2,
            },
            fixed: {
                zIndex: 1,
            },
        },
        MuiIconButton: {
            colorPrimary: {
                color: APP_COLOR_PRIMARY,
            },
        },
        MuiIcon: {
            colorPrimary: {
                color: APP_COLOR_PRIMARY,
            },
        },
        MuiSvgIcon: {
            colorPrimary: {
                color: APP_COLOR_PRIMARY,
            },
        },
        MuiLinearProgress: {
            colorPrimary: {
                color: APP_COLOR_PRIMARY,
            },
        },
        MuiChip: {
            root: {
                borderRadius: 999,
                fontWeight: 600,
                backgroundColor: CRM_COLORS.segmented,
                color: CRM_COLORS.text.primary,
            },
            colorPrimary: {
                backgroundColor: `${CRM_COLORS.status.info.background} !important`,
                color: CRM_COLORS.status.info.color,
            },
            deleteIconColorPrimary: {
                color: CRM_COLORS.status.info.color,
            },
            clickableColorPrimary: {
                '&:focus': {
                    backgroundColor: CRM_COLORS.status.info.background,
                },
                '&:hover': {
                    backgroundColor: CRM_COLORS.status.info.background,
                },
            },
        },
        // Inputs are boxed with their label above, whatever the variant the form asked for.
        MuiInput: {
            root: {
                backgroundColor: CRM_COLORS.surface,
                border: `1px solid ${CRM_COLORS.borderStrong}`,
                borderRadius: CRM_RADIUS.control,
                padding: '0 12px',
                minHeight: 42,
                fontSize: '0.9375rem',
                color: CRM_COLORS.text.primary,
                boxSizing: 'border-box',
                transition: 'border-color 0.15s ease',
                '&:hover': {
                    borderColor: hexToRGBA(APP_COLOR_PRIMARY, 0.5),
                },
                '&$focused': {
                    borderColor: APP_COLOR_PRIMARY,
                },
                '&$disabled': {
                    backgroundColor: CRM_COLORS.background,
                },
                '&$error': {
                    borderColor: CRM_COLORS.error,
                },
            },
            formControl: {
                'label + &': {
                    marginTop: 6,
                },
            },
            underline: {
                '&:before, &:after, &:hover:not($disabled):before': {
                    display: 'none',
                },
            },
        },
        MuiFormLabel: {
            root: {
                color: CRM_COLORS.text.label,
                fontSize: '0.875rem',
                fontWeight: 600,
                '&$focused': {
                    color: CRM_COLORS.text.label,
                },
                '&$error': {
                    color: CRM_COLORS.error,
                },
            },
        },
        MuiInputLabel: {
            formControl: {
                position: 'inherit !important',
                transform: 'none',
            },
            marginDense: {
                transform: 'none',
            },
            shrink: {
                transform: 'none',
                transformOrigin: 'top left',
            },
            // Outlined and filled fields get the same label above their box as the others.
            outlined: {
                transform: 'none !important',
                pointerEvents: 'auto',
                marginBottom: 6,
            },
            filled: {
                transform: 'none !important',
                pointerEvents: 'auto',
                marginBottom: 6,
            },
        },
        MuiFormHelperText: {
            root: {
                fontSize: '0.8125rem',
            },
        },
        MuiListItem: {
            //used for menu filter
            button: {
                '& > span:first-child': {
                    fontWeight: 500,
                    color: CRM_COLORS.text.primary,
                    fontSize: '0.9375rem',
                    textOverflow: 'ellipsis',
                },
            },
        },
        MuiTableCell: {
            // Tipografía unificada para TODAS las tablas de react-admin (listas + panel).
            head: {
                fontSize: '0.75rem',
                fontWeight: 700,
                color: CRM_COLORS.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                backgroundColor: CRM_COLORS.surfaceMuted,
                borderBottomColor: CRM_COLORS.border,
            },
            body: {
                fontSize: '0.875rem',
                color: CRM_COLORS.text.primary,
                '& .MuiTypography-root': {
                    fontSize: '0.875rem',
                    color: CRM_COLORS.text.primary,
                },
            },
            root: {
                borderBottomColor: CRM_COLORS.borderSoft,
                [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
                    padding: '8px 8px',
                    fontSize: '0.8125rem',
                },
            },
            sizeSmall: {
                [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
                    padding: '6px 8px',
                },
            },
        },
        MuiTablePagination: {
            root: {
                color: CRM_COLORS.text.secondary,
            },
            caption: {
                fontSize: '0.8125rem',
                color: CRM_COLORS.text.secondary,
            },
            select: {
                fontSize: '0.875rem',
            },
            input: {
                minHeight: 36,
                marginLeft: 4,
            },
        },
        RaEmpty: {
            icon: {
                width: '4rem',
                height: '4rem',
            },
            message: {
                color: CRM_COLORS.text.secondary,
                '& .MuiTypography-root': {
                    fontSize: '1.125rem',
                    fontWeight: 600,
                },
            },
        },
        MuiOutlinedInput: {
            notchedOutline: {
                top: 0,
                borderColor: CRM_COLORS.borderStrong,
                '& legend': {
                    display: 'none',
                },
            },
            root: {
                borderRadius: CRM_RADIUS.control,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: hexToRGBA(APP_COLOR_PRIMARY, 0.4),
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: APP_COLOR_PRIMARY,
                },
            },
        },
        MuiListItemIcon: {
            root: {
                minWidth: 'fit-content',
                marginRight: '5px',
                width: '20px !important',
                height: '20px !important',
                boxSizing: 'border-box',
            },
        },
        MuiTypography: {
            body1: {
                letterSpacing: 'unset',
                lineHeight: '1.46429em',
            },
        },
        MuiMenuItem: {
            root: {
                minHeight: 44,
                fontSize: '0.9375rem',
                color: CRM_COLORS.text.primary,
            },
        },
        MuiCardActions: {
            root: {
                boxSizing: 'border-box',
            },
        },
        MuiDialogActions: {
            root: {
                padding: '12px 20px 18px !important',
            },
        },
    },
});

export const ActionButton = withStyles({
    root: {
        '&:hover': {
            color: 'white',
            boxShadow: 'none',
            backgroundColor: APP_BUTTON_COLOR_PRIMARY,
        },
        padding: '9.5px 14px',
    },
})(Button);

export const TopToolbarButton = withStyles({
    root: {
        fontWeight: 'bold',
        color: APP_BUTTON_COLOR_PRIMARY,
    },
})(Button);

export const ActionFormButtonStyles = {
    root: {
        backgroundColor: 'white',
        paddingLeft: 21,
        paddingRight: 21,
        paddingTop: 10,
        paddingBottom: 10,
        boxShadow: 'none',
    },
    text: {
        color: APP_BUTTON_COLOR_PRIMARY,
        fontWeight: 'bold',
    },
};

export const OutlineVariantInputStyles = {
    inputMarginDense: {
        paddingTop: '6px !important',
        paddingBottom: '6px !important',
        fontSize: 'medium',
    },
    rootInputPropsInBackgroundColorPrimary: {
        paddingTop: '6px !important',
        paddingBottom: '6px !important',
        fontSize: 'medium',
        // backgroundColor: APP_BUTTON_COLOR_PRIMARY,
        color: APP_BUTTON_COLOR_PRIMARY,
    },
    notchedOutline: {
        borderColor: APP_BUTTON_COLOR_PRIMARY,
    },
    rootOutlined: {
        padding: '0px !important',
        color: APP_BUTTON_COLOR_PRIMARY,
        fontSize: 'medium',
    },
    rootOutlinedInBackgroundColorPrimary: {
        color: APP_BUTTON_COLOR_PRIMARY,
        padding: '0px !important',
        fontSize: 'medium',
    },
    rootInputPropsInBackgroundColorWhite: {
        paddingTop: '6px !important',
        paddingBottom: '6px !important',
        fontSize: 'medium',
        color: 'white',
        backgroundColor: 'white',
    },
    notchedOutlineColorWhite: {
        borderColor: 'white',
    },
    rootOutlinedColorWhite: {
        padding: '0px !important',
        color: 'white',
        fontSize: 'medium',
    },
    rootOutlinedInBackgroundColorPrimaryColorWhite: {
        padding: '0px !important',
        color: 'white',
        fontSize: 'medium',
    },
};

export const GridButtonShowViewStyles = {
    gridButtonOpenNewView: {
        position: 'absolute',
        right: 0,
    },
    buttonOpenNewView: {
        backgroundColor: APP_BUTTON_COLOR_PRIMARY,
        paddingLeft: 21,
        paddingRight: 21,
        paddingTop: 10,
        paddingBottom: 10,
        color: 'white',
        fontWeight: 'bold',
        '&:hover': {
            backgroundColor: APP_BUTTON_COLOR_PRIMARY,
            paddingLeft: 21,
            paddingRight: 21,
            paddingTop: 10,
            paddingBottom: 10,
            color: 'white',
            fontWeight: 'bold',
        },
    },
};

export const divContainerList = {
    main: {
        height: `calc(100% - ${APP_BAR_MAX_HEIGHT}px)`,
        marginTop: APP_BAR_MAX_HEIGHT,
        width: '100%',
        padding: '15px',
        paddingBottom: 0,
    },
};

export const ThresholdsFormStyles = {
    deleteIcon: {
        color: APP_BUTTON_COLOR_PRIMARY,
    },
    deleteIconDisabled: {
        color: 'gray',
    },
    addButtonRoot: {
        ...ActionFormButtonStyles.root,
    },
    addButtonRootText: {
        ...ActionFormButtonStyles.root,
        fontWeight: 'bold',
    },
    gridItemTitle: {
        marginTop: 4,
        marginBottom: 4,
    },
    itemsGroup: {
        marginBottom: 10,
    },
    dialogButtons: {
        marginBottom: 10,
    },
    inputColor: {
        marginTop: 15,
    },
    addIcon: {
        fontWeight: 'bold',
    },
};

export const DescriptionBar = (theme) => ({
    backDiv: {
        borderTop: '2px solid #C4D600',
        height: '80vh',
        width: '100%',
        overflow: 'auto',
    },
    backDivWithoutHeight: {
        borderTop: '2px solid #C4D600',
        // height: '50vh',
        width: '100%',
        // overflow: 'auto',
    },
    backDivWhite: {
        backgroundColor: 'white',
    },
    backDivWithShadow: {
        backgroundColor: '#f1f1f1 !important',
        boxShadow:
            '0px 1px 3px 0px rgba(0, 0, 0, 0.2),0px 1px 1px 0px rgba(0, 0, 0, 0.14),0px 2px 1px -1px rgba(0, 0, 0, 0.12)',
    },
    backHistoricDiv: {
        backgroundColor: '#f1f1f1',
    },
    root: {
        overflowY: 'auto',
        boxShadow:
            '0px 1px 3px 0px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.12)',
    },
    gridContainer: {
        backgroundColor: 'white',
        padding: '10px',
    },
    containerTitle: {
        // marginBottom: '8px',
        cursor: 'pointer',
    },
    typographyTitle: {
        textTransform: 'uppercase',
        fontWeight: '600',
        fontSize: theme.typography.pxToRem(13),
        color: 'rgba(0, 0, 0, 0.64)',
    },
    cardTitle: {
        fontSize: theme.typography.pxToRem(13),
        fontWeight: 'bold',
        marginBottom: 3,
    },
    cardSubheader: {
        fontSize: theme.typography.pxToRem(13),
        wordBreak: 'break-word',
    },
    cardRoot: {
        paddingTop: 7,
        paddingBottom: 5,
        paddingLeft: 3,
        paddingRight: 3,
    },
    fingersTableCellRoot: {
        padding: '5px',
    },
    links: {
        color: `${theme.palette.primary.main} !important`,
        margin: theme.spacing.unit,
        marginLeft: theme.spacing.unit * 3,
    },
    image: {
        marginLeft: theme.spacing.unit * 3,
    },
    imageButton: {
        width: 'fit-content',
        marginTop: theme.spacing.unit * 2,
        marginLeft: theme.spacing.unit * 3,
    },
    mcTableRoot: {
        // marginLeft: theme.spacing.unit*3,
        // width: 'auto',
        backgroundColor: theme.palette.grey[200],

        width: '100%',
        marginLeft: '0px',
    },
    mcEditButton: {
        width: '35px',
        padding: '0',
    },
    mcTableHeader: {
        paddingLeft: '8px !important',
        paddingRight: '4px !important',
    },
    mcTableCell: {
        paddingLeft: '8px !important',
        paddingRight: '4px !important',
        fontSize: theme.typography.pxToRem(12),
        textTransform: 'uppercase',
    },
    textTrunc: {
        color: 'rgba(0, 0, 0, 0.64)',
        textTransform: 'uppercase',
        overflow: 'hidden',
        position: 'relative',
        lineHeight: '1.2em',
        maxHeight: '2.4em',
        textAlign: 'justify',
        // marginRight: '-1em',
        // paddingRight: '1em',

        marginRight: '0px',
        paddingRight: '0px',

        '&:before': {
            content: "'...'",
            position: 'absolute',
            right: 0,
            bottom: 0,
        },
        '&:after': {
            content: "''",
            position: 'absolute',
            right: 0,
            width: '1em',
            height: '1em',
            marginTop: '0.2em',
            background: theme.palette.grey[200],
        },
    },
    expand: {
        transform: 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
        marginLeft: 'auto',
    },
    expandOpen: {
        transform: 'rotate(180deg)',
    },
    collapsedContainer: {
        maxHeight: '300px',
        overflowY: 'auto',
        width: '100%',
    },
    gridItemWithSize: {
        paddingTop: '0px !important',
        display: 'flow-root',
        overflowX: 'hidden',
    },
    divHistoric: {
        overflowX: 'hidden',
    },
    removeFlexbasis: {
        flexBasis: 'unset !important',
    },
    tableRowSelected: {
        backgroundColor: `${CRM_COLORS.rowSelected} !important`,
    },
});

/*export const DialogExtraData = (theme) => ({
    cardTitle: {
        fontSize: theme.typography.pxToRem(13),
        fontWeight: 'bold',
    },
    cardSubheader: {
        fontSize: theme.typography.pxToRem(13),
        wordBreak: 'break-word',
    },
    cardRoot: {
        paddingTop: '20px',
        paddingBottom: '5px',
        paddingLeft: '5px',
    },
    root: {
        overflowY: 'auto',
    },
    gridItem: {
        paddingTop: '20px !important',
    },
    simpleShowLayout: {
        paddingTop: '0px !important',
        paddingBottom: '0px !important',
        card: {
            fontWeight: 'bold',
            wordBreak: 'break-word',
        },
    },
});*/

export const ContentContainer = () => ({
    root: {
        margin: '0',
        WebkitFlexGrow: 1,
        flexGrow: 1,
    },
    rootOverflow: {
        overflowX: 'auto',
    },
    gridItem: {
        paddingTop: '0px !important',
    },
    gridItemWithSize: {
        paddingTop: '0px !important',
        display: 'flow-root',
        overflowX: 'hidden',
    },
});

export const tableStyles = () => ({
    tableRowSelected: {
        backgroundColor: `${CRM_COLORS.rowSelected} !important`,
    },
    cardList: {
        backgroundColor: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        minHeight: 400,
    },
    list: {
        height: 35,
    },
    checkbox: {
        padding: '0px 0px 0px 12px',
    },
    backDiv: {
        borderTop: '2px solid #C4D600',
        backgroundColor: 'white',
        height: '80vh',
        overflowY: 'scroll',
    },
});

export const actionsStyles = (theme) => ({
    icons: {
        marginRight: theme.spacing.unit,
        fontSize: `${theme.spacing.unit * 2}px`,
    },
    cardActions: {
        width: '100%',
        marginTop: 5,
        marginBottom: 5,
        justifyContent: 'flex-start',
        zIndex: 1,
    },
    menuListRoot: {
        padding: 0,
    },
    menuListItemRoot: {
        marginRight: 5,
    },
    menuRoot: {
        padding: '2px 10px 2px 5px',
    },
    menuIcon: {
        fontSize: 20,
    },
    menuTextRoot: {
        padding: 0,
        textTransform: 'uppercase',
        fontWeight: 'bold',
    },
    menuText: {
        fontSize: 13,
        fontWeight: 500,
    },
    rootCheckbox: {
        padding: 0,
        marginRight: theme.spacing.unit,
    },
    multiSelectLabel: {
        color: theme.palette.primary.main,
        textTransform: 'uppercase',
        fontSize: 13,
        fontWeight: 500,
    },
    multiSelectLabelCheckbox: {
        padding: 6,
        color: theme.palette.primary.main,
    },
    formControlLabelRoot: {
        marginLeft: -5,
        marginRight: 5,
    },
    dialogTitle: {
        padding: '17px 20px 0 20px',
        paddingBottom: 15,
    },
    dialogTitleText: {
        textTransform: 'uppercase',
        fontWeight: 'bold',
        fontSize: 17,
    },
    container: {
        display: 'grid',
        gridTemplateColumns: '30px auto',
        alignItems: 'center',
        columnGap: '7px',
    },
    dialogActions: {
        marginRight: 20,
        marginBottom: 20,
    },
    button: {
        paddingLeft: 30,
        paddingRight: 30,
        marginLeft: 10,
    },
    circularProgress: {
        marginTop: '20px',
        marginBottom: '20px',
        overflowY: 'hidden',
    },
    dialogContent: {
        paddingBottom: 0,
    },
    dialogText: {
        color: 'black',
        fontSize: 13,
    },
    buttonMenu: {
        paddingLeft: 10,
    },
});

export default theme;
